export type EmbedThemeColors = {
  primary: string;
  darkPrimary: string;
  accent: string;
  text: string;
  buttonText: string;
};

export type EmbedQueryOptions = {
  embed: boolean;
  lang: 'en' | 'kr' | null;
};

/** Defaults aligned with crimson-cresties-ecom theme tokens. */
export const DEFAULT_THEME_COLORS: EmbedThemeColors = {
  primary: '#19A8CC',
  darkPrimary: '#0C4479',
  accent: '#AEEFEB',
  text: '#000000',
  buttonText: '#FFFFFF',
};

const HEX_PATTERN = /^#?([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

const PARAM_ALIASES: Record<keyof EmbedThemeColors, string[]> = {
  primary: ['primary'],
  darkPrimary: ['darkPrimary', 'dark_primary', 'dark-primary'],
  accent: ['accent'],
  text: ['text'],
  buttonText: ['buttonText', 'button_text', 'button-text'],
};

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const raw = hex.replace('#', '');
  return {
    r: parseInt(raw.slice(0, 2), 16),
    g: parseInt(raw.slice(2, 4), 16),
    b: parseInt(raw.slice(4, 6), 16),
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) =>
    Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

export function normalizeHex(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!HEX_PATTERN.test(trimmed)) return null;
  const raw = trimmed.startsWith('#') ? trimmed.slice(1) : trimmed;
  if (raw.length === 3) {
    const expanded = raw
      .split('')
      .map((ch) => `${ch}${ch}`)
      .join('');
    return `#${expanded.toUpperCase()}`;
  }
  return `#${raw.toUpperCase()}`;
}

function readParam(params: URLSearchParams, aliases: string[]): string | null {
  const matched = aliases.find((key) => params.get(key) !== null);
  return matched ? params.get(matched) : null;
}

export function parseThemeColorsFromSearch(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
): EmbedThemeColors {
  const params = new URLSearchParams(search);
  const resolved = { ...DEFAULT_THEME_COLORS };

  (Object.keys(PARAM_ALIASES) as (keyof EmbedThemeColors)[]).forEach((key) => {
    const normalized = normalizeHex(readParam(params, PARAM_ALIASES[key]));
    if (normalized) resolved[key] = normalized;
  });

  return resolved;
}

export function parseEmbedOptionsFromSearch(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
): EmbedQueryOptions {
  const params = new URLSearchParams(search);
  const embedRaw = (params.get('embed') || '').toLowerCase();
  const embed = ['1', 'true', 'yes'].includes(embedRaw);
  const langRaw = (params.get('lang') || '').toLowerCase();
  const lang = langRaw === 'en' || langRaw === 'kr' ? langRaw : null;
  return { embed, lang };
}

/** Build a Mantine 10-shade palette with shade 6 as the brand color. */
export function generateColorShades(
  hex: string,
): [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
] {
  const { r, g, b } = hexToRgb(hex);
  const mix = (t: number) => {
    // t < 0 lightens toward white; t > 0 darkens toward black
    const channel = (c: number) => {
      if (t < 0) return Math.round(c + (255 - c) * -t);
      return Math.round(c * (1 - t));
    };
    return rgbToHex(channel(r), channel(g), channel(b));
  };

  return [
    mix(-0.95),
    mix(-0.85),
    mix(-0.7),
    mix(-0.5),
    mix(-0.3),
    mix(-0.15),
    hex.toUpperCase(),
    mix(0.15),
    mix(0.3),
    mix(0.45),
  ];
}

export function themeColorsToCssVars(
  colors: EmbedThemeColors,
): Record<string, string> {
  return {
    '--cc-primary': colors.primary,
    '--cc-dark-primary': colors.darkPrimary,
    '--cc-accent': colors.accent,
    '--cc-text': colors.text,
    '--cc-button-text': colors.buttonText,
  };
}
